// frontend/src/api/integrations.api.ts
import api from './axios';

export interface GoogleDriveStatus {
  connected: boolean;
  email?: string;
  name?: string;
  storage?: {
    limit: number;
    usage: number;
  };
}

export interface GoogleDriveUploadResult {
  uploaded: number;
  folderId: string;
  errors: string[];
}

export interface BulkExportProgress {
  running: boolean;
  totalProperties: number;
  completedProperties: number;
  currentProperty: string;
  totalFiles: number;
  uploadedFiles: number;
  skippedFiles: number;
  errors: Array<{ property: string; file?: string; error: string }>;
  startedAt: string | null;
  completedAt: string | null;
}

export interface Integration {
  id: number;
  integration_type: 'beds24' | 'hostex' | 'airbnb' | 'booking' | 'agoda' | 'google_drive';
  api_key_v1?: string;
  api_key_v2?: string;
  is_active: boolean;
  is_verified: boolean;
  last_verified_at?: string;
  last_sync_at?: string;
  sync_error?: string;
  settings_json?: any;
  created_at: string;
  updated_at: string;
}

export interface Beds24Property {
  propId: number;
  propName: string;
  propCode?: string;
  rooms: Beds24Room[];
}

export interface Beds24Room {
  roomId: number;
  roomName: string;
  propId: number;
}

export interface MyProperty {
  id: number;
  property_number: string;
  property_name?: string;
  property_type: string;
  region: string;
  bedrooms?: number;
  bathrooms?: number;
  deal_type: string;
  cover_photo?: string;
  beds24_prop_id?: number;
  beds24_room_id?: number;
  is_synced: boolean;
  creator_name: string;
  partner_id?: number;
  partner_name?: string;
}

export interface HostexChannel {
  channel_type: string;
  listing_id: string;
  currency?: string;
}

export interface HostexProperty {
  id: number;
  title: string;
  channels: HostexChannel[];
}

export interface MyHostexProperty {
  id: number;
  property_number: string;
  property_name?: string;
  property_type: string;
  region: string;
  bedrooms?: number;
  bathrooms?: number;
  deal_type: string;
  cover_photo?: string;
  beds24_prop_id?: number;
  beds24_room_id?: number;
  hostex_property_id?: number;
  is_synced: boolean;
  creator_name: string;
  partner_id?: number;
  partner_name?: string;
}

export const integrationsApi = {
  // Получить все интеграции
  getIntegrations: () =>
    api.get<{ success: boolean; data: Integration[] }>('/integrations'),

  // Получить конкретную интеграцию
  getIntegration: (type: string) =>
    api.get<{ success: boolean; data: Integration }>(`/integrations/${type}`),

  // Сохранить интеграцию
  saveIntegration: (type: string, data: {
    api_key_v1: string;
    api_key_v2?: string;
    settings_json?: any;
  }) =>
    api.post<{ success: boolean; message: string; data: { id: number } }>(
      `/integrations/${type}`,
      data
    ),

  // Удалить интеграцию
  deleteIntegration: (type: string) =>
    api.delete<{ success: boolean; message: string }>(`/integrations/${type}`),

  // ========== BEDS24 ==========

  // Проверить API ключ Beds24
  verifyBeds24: (api_key_v1: string) =>
    api.post<{ success: boolean; message: string }>(
      '/integrations/beds24/verify',
      { api_key_v1 }
    ),

  // Получить объекты из Beds24
  getBeds24Properties: () =>
    api.get<{ success: boolean; data: Beds24Property[] }>(
      '/integrations/beds24/properties'
    ),

  // Получить свои объекты для синхронизации
  getMyProperties: () =>
    api.get<{ success: boolean; data: MyProperty[] }>(
      '/integrations/beds24/my-properties'
    ),

  // Привязать объект к Beds24
  linkProperty: (data: {
    property_id: number;
    beds24_prop_id: number;
    beds24_room_id: number;
  }) =>
    api.post<{ success: boolean; message: string }>(
      '/integrations/beds24/link',
      data
    ),

  // Отвязать объект от Beds24
  unlinkProperty: (propertyId: number) =>
    api.delete<{ success: boolean; message: string }>(
      `/integrations/beds24/unlink/${propertyId}`
    ),

  // ========== HOSTEX ==========

  // Проверить токен Hostex (если api_key_v1 не передан — используется env-токен компании)
  verifyHostex: (api_key_v1?: string) =>
    api.post<{ success: boolean; message: string; data?: { propertiesCount: number } }>(
      '/integrations/hostex/verify',
      api_key_v1 ? { api_key_v1 } : {}
    ),

  // Получить объекты из Hostex для линковки
  getHostexProperties: () =>
    api.get<{ success: boolean; data: HostexProperty[] }>(
      '/integrations/hostex/properties'
    ),

  // Локальные объекты с признаком is_synced (по hostex_property_id)
  getMyHostexProperties: () =>
    api.get<{ success: boolean; data: MyHostexProperty[] }>(
      '/integrations/hostex/my-properties'
    ),

  // Привязать локальный property к Hostex property
  linkHostex: (data: { property_id: number; hostex_property_id: number }) =>
    api.post<{ success: boolean; message: string }>(
      '/integrations/hostex/link',
      data
    ),

  // Отвязать
  unlinkHostex: (propertyId: number) =>
    api.delete<{ success: boolean; message: string }>(
      `/integrations/hostex/unlink/${propertyId}`
    ),

  // Триггер немедленной синхронизации одного объекта из Hostex
  triggerHostexSync: (propertyId: number) =>
    api.post<{ success: boolean; message: string }>(
      `/integrations/hostex/sync/${propertyId}`
    ),

  // ========== GOOGLE DRIVE ==========

  // Get auth URL for Google Drive OAuth2
  googleDriveConnect: () =>
    api.get<{ success: boolean; data: { authUrl: string } }>(
      '/integrations/google-drive/connect'
    ),

  // Get Google Drive connection status
  googleDriveStatus: () =>
    api.get<{ success: boolean; data: GoogleDriveStatus }>(
      '/integrations/google-drive/status'
    ),

  // Disconnect Google Drive
  googleDriveDisconnect: () =>
    api.delete<{ success: boolean; message: string }>(
      '/integrations/google-drive/disconnect'
    ),

  // Upload property photos to Google Drive
  googleDriveUploadPhotos: (propertyId: number) =>
    api.post<{ success: boolean; message: string; data: GoogleDriveUploadResult }>(
      '/integrations/google-drive/upload-photos',
      { propertyId }
    ),

  // Start bulk export of all properties to Google Drive
  googleDriveBulkExport: () =>
    api.post<{ success: boolean; message: string }>(
      '/integrations/google-drive/bulk-export'
    ),

  // Get bulk export progress
  googleDriveBulkExportProgress: () =>
    api.get<{ success: boolean; data: BulkExportProgress }>(
      '/integrations/google-drive/bulk-export/progress'
    ),
};